import { defineComponent, ref, computed, watch, reactive, nextTick } from "vue";
import { storeToRefs } from "pinia";
import IconButton from "../common/base/IconButton.vue.mjs";
import SvgIcon from "../common/base/SvgIcon.vue.mjs";
import BasicBeautyIcon from "../common/icons/BasicBeautyIcon.vue.mjs";
import { useI18n } from "../../locales/index.mjs";
import "../../services/main.mjs";
import { roomService } from "../../services/roomService.mjs";
import { TRTCVideoMirrorType, TRTCVideoRotation, TRTCVideoFillMode } from "@tencentcloud/tuiroom-engine-js";
import { isMobile } from "../../utils/environment.mjs";
import { throttle } from "../../utils/utils.mjs";
import "mitt";
import "../../services/manager/roomActionManager.mjs";
import "@tencentcloud/tui-core";
import { MetricsKey } from "../../services/manager/dataReportManager.mjs";
import Dialog from "../common/base/Dialog/index.mjs";
import TuiButton from "../common/base/Button.vue.mjs";
import Slider from "../common/base/Slider.vue.mjs";
import CloseBeautyIcon from "../common/icons/CloseBeautyIcon.vue.mjs";
import SmootherBeautyIcon from "../common/icons/SmootherBeautyIcon.vue.mjs";
import WhiteningBeautyIcon from "../common/icons/WhiteningBeautyIcon.vue.mjs";
import RuddyBeautyIcon from "../common/icons/RuddyBeautyIcon.vue.mjs";
import ResetIcon from "../common/icons/ResetIcon.vue.mjs";
import CompareIcon from "../common/icons/CompareIcon.vue.mjs";
import useGetRoomEngine from "../../hooks/useRoomEngine.mjs";
import { useBasicStore } from "../../stores/basic.mjs";
import { TRTCBeautyStyle } from "../../constants/room.mjs";
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "BasicBeauty",
  setup(__props) {
    const roomEngine = useGetRoomEngine();
    const basicStore = useBasicStore();
    const { isLocalStreamMirror } = storeToRefs(basicStore);
    const { t } = useI18n();
    const isShowDegree = ref(false);
    const isBeautyStarted = ref(false);
    const componentConfig = roomService.componentManager.getComponentConfig("BasicBeauty");
    const isAllowed = computed(
      () => {
        var _a;
        return (_a = roomService.roomStore.localStream) == null ? void 0 : _a.hasVideoStream;
      }
    );
    watch(isLocalStreamMirror, async (val) => {
      var _a;
      const trtcCloud = (_a = roomEngine.instance) == null ? void 0 : _a.getTRTCCloud();
      if (!isMobile) {
        await (trtcCloud == null ? void 0 : trtcCloud.setLocalRenderParams({
          mirrorType: val ? TRTCVideoMirrorType.TRTCVideoMirrorType_Enable : TRTCVideoMirrorType.TRTCVideoMirrorType_Disable,
          rotation: TRTCVideoRotation.TRTCVideoRotation0,
          fillMode: TRTCVideoFillMode.TRTCVideoFillMode_Fill
        }));
      }
    });
    const appliedBasicBeautyItem = ref("close");
    const selectBasicBeautyItem = ref(
      "close"
    );
    const isDialogVisible = ref(false);
    const isShowResetDialog = ref(false);
    const isLoading = ref(false);
    const sliderValue = ref(0);
    const beautyLevels = reactive({
      smoother: 0,
      whitening: 0,
      ruddy: 0
    });
    const savedBeautyLevels = reactive({
      smoother: 0,
      whitening: 0,
      ruddy: 0
    });
    const beautyOptionList = [
      { value: "close", text: "Close", icon: CloseBeautyIcon },
      { value: "smoother", text: "Smoother", icon: SmootherBeautyIcon },
      { value: "whitening", text: "Whitening", icon: WhiteningBeautyIcon },
      { value: "ruddy", text: "Ruddy", icon: RuddyBeautyIcon }
    ];
    const openBeautySettingPanel = async () => {
      var _a;
      roomService.basicBeauty.initBasicBeauty();
      isDialogVisible.value = true;
      isLoading.value = true;
      await nextTick();
      await ((_a = roomService.roomEngine.instance) == null ? void 0 : _a.startCameraDeviceTest({
        view: "test-preview"
      }));
      isLoading.value = false;
      roomService.dataReportManager.reportCount(MetricsKey.setBasicBeauty);
    };
    const closeBeautySettingPanel = async () => {
      var _a;
      isDialogVisible.value = false;
      Object.assign(beautyLevels, savedBeautyLevels);
      await onBeautyPropertyClick(appliedBasicBeautyItem.value);
      (_a = roomService.roomEngine.instance) == null ? void 0 : _a.stopCameraDeviceTest();
      selectBasicBeautyItem.value = appliedBasicBeautyItem.value;
    };
    const saveBeautySetting = async () => {
      if (!isAllowed.value) return;
      appliedBasicBeautyItem.value = selectBasicBeautyItem.value;
      if (selectBasicBeautyItem.value === "close") {
        await roomService.basicBeauty.setBasicBeauty(
          TRTCBeautyStyle.TRTCBeautyStyleNature,
          0,
          0,
          0
        );
      } else {
        await roomService.basicBeauty.setBasicBeauty(
          TRTCBeautyStyle.TRTCBeautyStyleNature,
          Math.floor(beautyLevels.smoother / 100 * 9),
          Math.floor(beautyLevels.whitening / 100 * 9),
          Math.floor(beautyLevels.ruddy / 100 * 9)
        );
      }
      Object.assign(savedBeautyLevels, beautyLevels);
      closeBeautySettingPanel();
    };
    const startBeautyTest = async () => {
      await roomService.basicBeauty.setTestBasicBeauty(
        TRTCBeautyStyle.TRTCBeautyStyleNature,
        Math.floor(beautyLevels.smoother / 100 * 9),
        Math.floor(beautyLevels.whitening / 100 * 9),
        Math.floor(beautyLevels.ruddy / 100 * 9)
      );
      isBeautyStarted.value = !(beautyLevels.smoother === 0 && beautyLevels.whitening === 0 && beautyLevels.ruddy === 0);
    };
    const closeBeautyTest = async () => {
      await roomService.basicBeauty.setTestBasicBeauty(
        TRTCBeautyStyle.TRTCBeautyStyleNature,
        0,
        0,
        0
      );
    };
    const throttleStartBeautyTest = throttle(startBeautyTest, 300);
    watch(sliderValue, async (newValue) => {
      if (selectBasicBeautyItem.value === "smoother") {
        beautyLevels.smoother = newValue;
      }
      if (selectBasicBeautyItem.value === "whitening") {
        beautyLevels.whitening = newValue;
      }
      if (selectBasicBeautyItem.value === "ruddy") {
        beautyLevels.ruddy = newValue;
      }
      await throttleStartBeautyTest();
    });
    const onBeautyPropertyClick = async (type) => {
      if (type === "close") {
        if (isBeautyStarted.value) {
          await closeBeautyTest();
        }
        beautyLevels.smoother = 0;
        beautyLevels.whitening = 0;
        beautyLevels.ruddy = 0;
        sliderValue.value = 0;
      } else {
        sliderValue.value = beautyLevels[type];
      }
      isShowDegree.value = type !== "close";
      selectBasicBeautyItem.value = type;
      isLoading.value = false;
    };
    const handleShowResetDialog = () => {
      isShowResetDialog.value = true;
    };
    const resetBeautyProperties = async () => {
      await closeBeautyTest();
      beautyLevels.smoother = 0;
      beautyLevels.whitening = 0;
      beautyLevels.ruddy = 0;
      sliderValue.value = 0;
      isShowResetDialog.value = false;
    };
    const handleMouseDown = async () => {
      await closeBeautyTest();
    };
    const handleMouseUp = async () => {
      await startBeautyTest();
    };
    return { __sfc: true, roomEngine, basicStore, isLocalStreamMirror, t, isShowDegree, isBeautyStarted, componentConfig, isAllowed, appliedBasicBeautyItem, selectBasicBeautyItem, isDialogVisible, isShowResetDialog, isLoading, sliderValue, beautyLevels, savedBeautyLevels, beautyOptionList, openBeautySettingPanel, closeBeautySettingPanel, saveBeautySetting, startBeautyTest, closeBeautyTest, throttleStartBeautyTest, onBeautyPropertyClick, handleShowResetDialog, resetBeautyProperties, handleMouseDown, handleMouseUp, IconButton, SvgIcon, BasicBeautyIcon, Dialog, TuiButton, Slider, ResetIcon, CompareIcon };
  }
});
export {
  _sfc_main as default
};
